import { useMemo, useState, FC, Fragment } from 'react';
import { Modal } from 'react-bootstrap';
import { Pie, PieChart, Cell, ResponsiveContainer } from 'recharts';
import cn from 'classnames';
import { formatMoney } from '@blisspointmedia/bpm-types/dist/formatUtils';
import './ChannelMixModal.scss';
import { Button } from '@Components/Button';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Dropdown } from '@Components/Dropdown';
import { Appearance, Design, Placement, Size } from '@Components/designConstants';
import { MdClose } from 'react-icons/md';
import { formatPercentRounded } from '../../utils/hroasUtils';
import { createChannelColorMap } from '../utils/efficientFrontierUtils';
import { ChannelMixDataPoint } from '../../hroas-types';

interface ChannelMixModalProps {
  show: boolean;
  onHide: () => void;
  dataPoint: ChannelMixDataPoint | null;
  channelGroups: string[];
}

export const ChannelMixModal: FC<ChannelMixModalProps> = ({
  show,
  onHide,
  dataPoint,
  channelGroups
}) => {
  const [sortBy, setSortBy] = useState<string>('highToLow');
  const [hoveredValue, setHoveredValue] = useState<string | undefined>();
  const channelColorMap = useMemo(() => createChannelColorMap(channelGroups), [channelGroups]);

  const pieFormattedData = useMemo(() => {
    if (!dataPoint) {
      return [];
    }

    return channelGroups
      .map(group => {
        const value = dataPoint[group] as number;
        if (value === undefined || value === 0) {
          return null;
        }
        return {
          name: group,
          value
        };
      })
      .filter(item => item !== null);
  }, [dataPoint, channelGroups]);

  const legendFormattedData = useMemo(() => {
    if (!dataPoint) {
      return [];
    }

    const total = channelGroups.reduce((sum, group) => {
      const value = dataPoint[group] as number;
      return sum + (value || 0);
    }, 0);

    return channelGroups
      .map(group => {
        const value = dataPoint[group] as number;
        if (value === undefined || value === 0) {
          return null;
        }
        const percentage = total > 0 ? (value / total) * 100 : 0;
        return {
          name: group,
          rawValue: value,
          rawPercentage: percentage,
          monetaryValue: formatMoney(value, 0),
          percentageValue: formatPercentRounded(percentage),
          color: channelColorMap[group]
        };
      })
      .filter(item => item !== null);
  }, [dataPoint, channelGroups, channelColorMap]);

  const sortedLegendData = useMemo(() => {
    const data = [...legendFormattedData];
    switch (sortBy) {
      case 'abc':
        return data.sort((a, b) => a.name.localeCompare(b.name));
      case 'highToLow':
        return data.sort((a, b) => b.rawValue - a.rawValue);
      default:
        return data;
    }
  }, [legendFormattedData, sortBy]);

  if (!dataPoint) {
    return null;
  }

  return (
    <Modal show={show} centered onHide={onHide} size='lg' className='ef-channel-mix-modal'>
      <Modal.Header>
        <div className='cmm-title-container'>
          <span className='main-title'>Channel Mix</span>
          <span className='total-spend'>{formatMoney(dataPoint.totalBudget, 0)}</span>
        </div>
        <div className='cmm-title-actions'>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            options={[
              { label: 'Abc', value: 'abc' },
              { label: 'High to Low', value: 'highToLow' }
            ]}
            onChange={value => setSortBy(value)}
            value={sortBy}
            label='Sort'

            size={Size.SM}
          />
          <DownloadDropdown size={Size.SM} onClickOptions={[() => {}, () => {}]} />
          <Button
            onClick={onHide}
            design={Design.NEUTRAL}
            size={Size.SM}
            appearance={Appearance.EMPTY}
          >
            <MdClose />
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className='cmm-content'>
          <div className='cmm-legend-container'>
            <div className='cmm-legend-body'>
              {sortedLegendData.map(item => {
                const itemClasses = cn({
                  'ef-hovered': item.name === hoveredValue,
                  'ef-unhovered': Boolean(hoveredValue) && item.name !== hoveredValue
                });
                return (
                  <Fragment key={item.name}>
                    <div
                      className={cn('cmm-legend-label-wrapper', itemClasses)}
                      onMouseEnter={() => setHoveredValue(item.name)}
                      onMouseLeave={() => setHoveredValue(undefined)}
                    >
                      <div className='cmm-legend-color' style={{ backgroundColor: item.color }} />
                      <span className='cmm-legend-label'>{item.name}</span>
                    </div>
                    <div
                      className={cn('cmm-legend-percent', itemClasses)}
                      onMouseEnter={() => setHoveredValue(item.name)}
                      onMouseLeave={() => setHoveredValue(undefined)}
                    >
                      {item.percentageValue}
                    </div>
                    <div
                      className={cn('cmm-legend-value', itemClasses)}
                      onMouseEnter={() => setHoveredValue(item.name)}
                      onMouseLeave={() => setHoveredValue(undefined)}
                    >
                      {item.monetaryValue}
                    </div>
                  </Fragment>
                );
              })}
            </div>
            <div className='cmm-legend-footer'>
              <span className='cmm-legend-footer-label'>Total Spend</span>
              <span className='cmm-legend-footer-value'>
                {formatMoney(dataPoint.totalBudget, 0)}
              </span>
            </div>
          </div>
          <div className='cmm-chart-container'>
            <ResponsiveContainer width={400} height='100%'>
              <PieChart>
                <Pie
                  data={pieFormattedData}
                  isAnimationActive={false}
                  startAngle={90}
                  endAngle={-270}
                  cx='50%'
                  cy='50%'
                  innerRadius='25%'
                  outerRadius='80%'
                  dataKey='value'
                  onMouseEnter={(slice: any) => {
                    setHoveredValue(slice?.name);
                  }}
                  onMouseLeave={() => {
                    setHoveredValue(undefined);
                  }}
                >
                  {pieFormattedData.map(({ name }) => (
                    <Cell
                      className={cn('cmm-pie-cell', {
                        'ef-hovered': name === hoveredValue,
                        'ef-unhovered': Boolean(hoveredValue) && name !== hoveredValue
                      })}
                      key={`cell-${name}`}
                      fill={channelColorMap[name]}
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
